import 'core-js/stable';
import 'regenerator-runtime/runtime';
import { actionTypes as formActions, change } from 'redux-form';
import { push } from 'react-router-redux';
import { all, call, put, select, take, takeLatest } from 'redux-saga/effects';
import { authSelector } from 'sagas/login';

import { STEP_IDS, STEPS } from 'constants/Steps';

import actions from 'actions';
import {
    ESTIMA_BASE_URL,
    ESTIMA_FORM,
    FORM_ADDRESS_NAME,
    FORM_CITY_NAME,
    FORM_ZIP,
    GA_ESTIMA_FORM_CATEGORY,
    GA_UPDATE_CATEGORY,
    LOGIN_INPUT_PREFIX,
    MAX_ADDRESS_WORD_DISTANCE,
    ZOOM_FOR_CITY,
    ESTIMA_FUNNEL_CONFIRMATION_DELAY,
} from 'constants/Global';
import { isOfflineWithToken, redirectToHabitationValuation } from 'helpers/Estima';
import { hasPhoneRetrieval, isBarometreStepNumber, isValidAddress } from 'helpers/FormInformations';
import { gaEvent, gaPageView, gaSet, getSource, setSource } from 'helpers/Ga';
import { getValueFromPath } from 'helpers/ObjectUtils';
import { getSearchParamsAsObject } from 'helpers/Referrer';
import { getEditDistance } from 'helpers/StringUtils';
import AuthService from 'services/AuthService';
import EstimaFormService from 'services/EstimaFormService';
import EstimaService from 'services/EstimaService';
import FacebookService from 'services/FacebookService';
import GeoService from 'services/GeoService';
import HabitationsService from 'services/HabitationsService';
import RealyticsService from 'services/RealyticsService';
import { computeNextStepNumber, getFields, isLastStep } from 'helpers/Steps';

import { thorPushEvent } from 'helpers/Ga4';

/**
 * Select steps from state (used with redux-saga/effects.select)
 * @param {Object} state current state
 */
export const stepsSelector = (state) => state.steps;

/**
 * Select form from state (used with redux-saga/effects.select)
 * @param {Object} state current state
 */
export const formSelector = (state) => state.form[ESTIMA_FORM];

/**
 * Select address data from state (used with redux-saga/effects.select)
 * @param {Object} state current state
 */
export const addressSelector = (state) => state.address;

/**
 * Sending GA events for form.
 * @yield {Action} Sending GA landed
 * @yield {Action} First redux-form action
 * @yield {Action} Sending GA started
 */
export function* gaFormEventSaga() {
    yield all([
        call(gaSet, { contentGroup1: 'estima/form' }),
        call(gaSet, { dimension4: 'estima/form' }),
    ]);

    yield call(gaPageView, `${ESTIMA_BASE_URL}/form/${STEPS[0]?.taggingId}`);

    yield call(gaEvent, {
        category: GA_ESTIMA_FORM_CATEGORY,
        action: 'estima:form:landed',
        label: `from:${getSource()}`,
    });

    yield take([formActions.CHANGE, formActions.BLUR]);

    yield call(gaEvent, {
        category: GA_ESTIMA_FORM_CATEGORY,
        action: 'estima:form:started',
        label: `from:${getSource()}`,
    });
}

/**
 * Sending GA events for barometre.
 * @yield {Action} Sending GA landed
 * @yield {Action} First redux-form action
 * @yield {Action} Sending GA started
 */
export function* gaBarometreEventSaga() {
    yield all([
        call(gaSet, { contentGroup1: 'estima/update' }),
        call(gaSet, { dimension4: 'estima/update' }),
    ]);
    yield all([
        call(gaPageView, `${ESTIMA_BASE_URL}/update`),
        call(gaEvent, {
            category: GA_UPDATE_CATEGORY,
            action: 'estima:update:landed',
            label: `from:${getSource()}`,
        }),
    ]);

    yield take([formActions.CHANGE, formActions.BLUR]);

    yield call(gaEvent, {
        category: GA_UPDATE_CATEGORY,
        action: 'estima:update:started',
        label: `from:${getSource()}`,
    });
}

/**
 * Sending GA events for barometre retrieve phone step.
 * @yield {Action} Sending GA landed
 * @yield {Action} First redux-form action
 * @yield {Action} Sending GA started
 */
export function* gaBarometreRetrievePhoneStepEventSaga() {
    yield call(gaEvent, {
        category: GA_UPDATE_CATEGORY,
        action: 'estima:update:retrieve_phone:landed',
        label: `from:${getSource()}`,
    });

    yield take([formActions.CHANGE, formActions.BLUR]);

    yield call(gaEvent, {
        category: GA_UPDATE_CATEGORY,
        action: 'estima:update:retrieve_phone:started',
        label: `from:${getSource()}`,
    });

    yield take(formActions.SUBMIT);

    yield call(gaEvent, {
        category: GA_UPDATE_CATEGORY,
        action: 'estima:update:retrieve_phone:submit_attempt',
        label: `from:${getSource()}`,
    });

    yield take(formActions.SET_SUBMIT_SUCCEEDED);

    yield call(gaEvent, {
        category: GA_UPDATE_CATEGORY,
        action: 'estima:update:retrieve_phone:submit_success',
        label: `from:${getSource()}`,
    });
}

/**
 * Sending GA events for submit.
 * @yield {String} Retrieve steps configuration
 * @yield {Action} Send GA submit_attempt
 */
export function* gaFormSubmitSaga() {
    const steps = yield select(stepsSelector);
    const isUpdate = isBarometreStepNumber(steps.number);
    let source = isUpdate ? 'update' : 'form';
    const events = steps.events[source] || {};

    if (!events.submit_attempt) {
        const yieldArray = [
            put(
                actions.gaSubmit(
                    isUpdate ? GA_UPDATE_CATEGORY : GA_ESTIMA_FORM_CATEGORY,
                    source,
                    'submit_attempt',
                ),
            ),
        ];

        if (steps.id === STEP_IDS.retrievePhone) {
            yieldArray.push(
                put(
                    actions.gaSubmit(
                        GA_ESTIMA_FORM_CATEGORY,
                        `${source}:${STEPS[steps.number - 1].taggingId}`,
                        'submit_attempt',
                    ),
                ),
            );
        }

        yield all(yieldArray);
    }
}

/**
 * Send GA Event when going to previous step.
 * @yield {String} Retrieve step id
 * @yield {Action} Changing step and sending GA event if step has id
 */
export function* previousStepSaga() {
    const form = yield select(formSelector);
    const steps = yield select(stepsSelector);
    const address = yield select(addressSelector);
    const auth = yield select(authSelector);
    const { taggingId } = STEPS[steps.number - 1];
    const actionsArray = [];

    if (taggingId) {
        actionsArray.push(put(actions.gaStep(taggingId, 'back')));
    }

    actionsArray.push(
        put(
            actions.changeStep(
                computeNextStepNumber(STEPS, steps.number, form.values, {
                    suggestedAddresses: address.suggestedAddresses,
                    modifier: -1,
                    auth,
                }),
            ),
        ),
    );

    yield all(actionsArray);
}

/**
 * Send GA Event when going to next step.
 * @yield {String} Receive step id
 * @yield {String} Sending GA event and changing step
 */
export function* nextStepSaga() {
    const form = yield select(formSelector);
    const steps = yield select(stepsSelector);
    const address = yield select(addressSelector);
    const auth = yield select(authSelector);
    const { taggingId } = STEPS[steps.number - 1];

    const actionsArray = [];

    if (taggingId) {
        const current = steps.events[taggingId] || {};
        if (!current.submit_success) {
            actionsArray.push(
                put(actions.gaStep(taggingId, 'submit_attempt')),
                put(actions.gaStep(taggingId, 'submit_success')),
            );
        }
    }

    actionsArray.push(
        put(
            actions.changeStep(
                computeNextStepNumber(STEPS, steps.number, form.values, {
                    suggestedAddresses: address.suggestedAddresses,
                    auth,
                }),
            ),
        ),
    );

    yield all(actionsArray);
}

/**
 * Send GA Event for given step.
 * @param {Object} action Action performed
 * @yield {Action} Sending GA event
 */
export function* gaStepSaga(action) {
    yield call(gaEvent, {
        category: GA_ESTIMA_FORM_CATEGORY,
        action: `estima:form:${action.step}:${action.event}`,
        label: `from:${getSource()}`,
    });
}

/**
 * Send GA Event for global submit.
 * @param {Object} action Action performed
 * @yield {Action} Sending GA event
 */
export function* gaSubmitSaga(action) {
    yield call(gaEvent, {
        category: action.category,
        action: `estima:${action.source}:${action.event}`,
        label: `from:${getSource()}`,
    });
}

/**
 * Sending GA events if not already done
 * @yield {Action} Sending GA landed
 * @yield {Action} First redux-form action
 * @yield {Action} Sending GA started
 */
export function* gaEventStepSaga() {
    const form = yield select(formSelector);
    const steps = yield select(stepsSelector);

    thorPushEvent({
        eventType: 'page_view',
        payload: { form, steps },
    });

    if (isBarometreStepNumber(steps.number)) {
        return;
    }

    const { taggingId } = STEPS[steps.number - 1];

    if (taggingId) {
        const current = steps.events[taggingId] || {};

        if (steps.number > 1) {
            yield call(gaPageView, `${ESTIMA_BASE_URL}/form/${taggingId}`);
        }

        if (!current.landed) {
            const yieldArray = [put(actions.gaStep(taggingId, 'landed'))];

            if (
                steps.id === STEP_IDS.retrievePhone &&
                form.values[`${LOGIN_INPUT_PREFIX}cell_phone`] !== ''
            ) {
                yieldArray.push(put(actions.gaStep(taggingId, 'landed:prefilled')));
            }

            yield all(yieldArray);
        }

        if (!current.started) {
            yield take([formActions.CHANGE, formActions.BLUR]);
            yield put(actions.gaStep(taggingId, 'started'));
        }
    }
}

/**
 * Receive form validation and enabled next step or not.
 * @yield {Action} Retrieving form from state
 * @yield {Action} Retrieving step from state
 * @yield {Action} Changing step validation
 */
export function* formValidationSaga(action) {
    const steps = yield select(stepsSelector);
    const form = yield select(formSelector);

    let stepConfig;

    if (steps.number > 0) {
        stepConfig = STEPS[steps.number - 1];
    }

    if (stepConfig && stepConfig.disableAsyncValidate) {
        return;
    }

    const validationErrors = Object.keys(action.payload || {});

    if (validationErrors.indexOf('_error') !== -1) {
        yield put(actions.validStep(steps.number, false));
    } else {
        const stepErrors = stepConfig
            ? validationErrors.filter(
                  (key) => getFields(stepConfig, form.values).indexOf(key) !== -1,
              )
            : validationErrors;

        if (stepErrors.length === 0) {
            yield put(actions.validStep(steps.number, true));
        } else {
            yield put(actions.validStep(steps.number, false));
        }
    }
}

/**
 * Check user authentication.
 * @yield {Action} Check authentication
 * @yield {Action} Dispatch result
 */
export function* checkAuthSaga() {
    try {
        const result = yield call(AuthService.check);

        const options = isOfflineWithToken() ? { needLogin: true } : {};
        const yieldActions = [put(actions.checkAuthReceived({ ...result.data, ...options }))];

        if (getValueFromPath(result, 'data.contact.firstname')) {
            yieldActions.push(
                put(
                    change(
                        ESTIMA_FORM,
                        `${LOGIN_INPUT_PREFIX}firstname`,
                        result.data.contact.firstname,
                    ),
                ),
            );
        }

        if (getValueFromPath(result, 'data.contact.lastname')) {
            yieldActions.push(
                put(
                    change(
                        ESTIMA_FORM,
                        `${LOGIN_INPUT_PREFIX}lastname`,
                        result.data.contact.lastname,
                    ),
                ),
            );
        }

        if (getValueFromPath(result, 'data.contact.phone.value')) {
            yieldActions.push(
                put(change(ESTIMA_FORM, 'user_phone', result.data.contact.phone.value)),
                put(change(ESTIMA_FORM, '_initial_user_phone', result.data.contact.phone.value)),
            );
        }

        yield all(yieldActions);
    } catch (e) {
        if (e.response && e.response.status === 403) {
            yield put(actions.checkAuthReceived({ ...e.response.data, needLogin: true }));
        } else {
            yield put(actions.checkAuthFailed(e));
        }
    }
}

/**
 * Call user authentication.
 * @yield {Action} Call authentication
 * @yield {Action} Dispatch result
 */
export function* callAuthSaga(action) {
    if (action.step > 0 && STEPS[action.step - 1].checkAuth) {
        yield* checkAuthSaga(action);
    }
}

/**
 * Check zone media.
 * @yield {Action} Check zone media
 * @yield {Action} Dispatch result
 */
export function* checkZoneMediaSaga() {
    try {
        const {
            values: { coordinates },
        } = yield select(formSelector);
        const result = yield call(EstimaFormService.isZoneMedia, coordinates.lat, coordinates.lng);
        const isZoneMedia = !result.data.is_zone_transac;

        yield put(actions.checkZoneMediaReceived(isZoneMedia, true));
    } catch (e) {
        yield put(actions.checkZoneMediaFailed(e));
    }
}

/**
 * Call zone media.
 * @yield {Action} Call zone media
 * @yield {Action} Dispatch result
 */
export function* callZoneMediaSaga(action) {
    if (action.step > 0 && STEPS[action.step - 1].checkZoneMedia) {
        yield* checkZoneMediaSaga(action);
    }
}

/**
 * Call get habitations.
 * @yield {Action} Call service
 * @yield {Action} Dispatch received values
 */
export function* getHabitationsSaga() {
    try {
        const response = yield call(HabitationsService.getHabitations);
        yield put(actions.getHabitationsReceived(response.data));
    } catch (err) {
        if (getValueFromPath(err, 'response.status') !== 401) {
            yield put(actions.getHabitationsFailed(err));
        } else {
            yield put(actions.getHabitationsReceived(null));
        }
    }
}

/**
 * Retrieve Estima from API.
 * @param {Object} action GET_ESTIMA action with id
 * @yield {Action} Retrieve estima from API.
 * @yield {Action} Spread retrieved information into Redux.
 */
export function* getEstimaSaga(action) {
    try {
        const estima = yield call(EstimaService.getEstima, action.id);
        yield all([put(actions.getEstimaReceived(estima.data)), put(action.onFullFill)]);
    } catch (e) {
        yield put(actions.getEstimaFailed(e));
    }
}

/**
 * Check if current step is the last one.
 * @yield {Action} Retrieve form values
 * @yield {Action} Retrieve steps state
 * @yield {Action} Retrieve auth state
 * @yield {Action} Dispatch action for changing last and eventually
 */
export function* isLastStepSaga() {
    const form = yield select(formSelector);
    const steps = yield select(stepsSelector);
    const auth = yield select(authSelector);
    const address = yield select(addressSelector);

    if (isLastStep({ form, step: steps, auth, address })) {
        const actionsArray = [put(actions.setLastStep(true))];

        if (!steps.events.global || !steps.events.global.auth) {
            if (auth.needLogin) {
                actionsArray.push(
                    call(gaEvent, {
                        category: GA_ESTIMA_FORM_CATEGORY,
                        action: 'estima:auth:required',
                        label: `from:${getSource()}`,
                    }),
                );
            } else {
                actionsArray.push(
                    call(gaEvent, {
                        category: GA_ESTIMA_FORM_CATEGORY,
                        action: 'estima:auth:skipped',
                        label: `from:${getSource()}`,
                    }),
                );
            }
        }

        yield all(actionsArray);
    } else {
        yield put(actions.setLastStep(false));
    }
}

/**
 * Go to steps form from update.
 * @yield {Action} Send GA event.
 * @yield {Action} Change source label.
 * @yield {Action} Change url.
 */
export function* goToStepsFormSaga(action) {
    const { token } = getSearchParamsAsObject();

    const estimaToken = token ? `&token=${token}` : '';

    yield all([
        call(gaEvent, {
            category: GA_UPDATE_CATEGORY,
            action: 'go:estima:form',
            label: 'estima:update:update_estima_fields_button',
        }),
        call(setSource, 'barometre'),
        put(
            push(
                `${ESTIMA_BASE_URL}/form?item_city_name=${encodeURIComponent(
                    action.values[FORM_CITY_NAME],
                )}&item_city_id=${encodeURIComponent(
                    action.values._city_id,
                )}&item_zip=${encodeURIComponent(
                    action.values[FORM_ZIP],
                )}&item_address_name=${encodeURIComponent(
                    action.values[FORM_ADDRESS_NAME],
                )}${estimaToken}`,
            ),
        ),
    ]);
}

/**
 * Handle submit success of Estima
 * @yield {Action} Send GA Event for success form.
 * @yield {Action} Redirect to fake animation
 */
export function* submitSuccessSaga(action) {
    const steps = yield select(stepsSelector);
    const { values } = yield select(formSelector);
    const isUpdate = isBarometreStepNumber(steps.number);
    let source = isUpdate ? 'update' : 'form';
    const events = steps.events[source] || {};

    const redirect = steps.id === STEP_IDS.login || steps.id === STEP_IDS.retrievePhone || isUpdate;
    const gaCompletedTimeout = () => {
        setTimeout(() => {
            if (redirect) {
                redirectToHabitationValuation(action.habitationId);
            }
        }, ESTIMA_FUNNEL_CONFIRMATION_DELAY);
    };

    if (!events.submit_success) {
        const yieldArray = [
            call(RealyticsService.trackFormSubmit, values),
            call(gaEvent, {
                category: isUpdate ? GA_UPDATE_CATEGORY : GA_ESTIMA_FORM_CATEGORY,
                action: `estima:${source}:submit_success`,
                label: `from:${getSource()}`,
            }),
            call(FacebookService.trackEstimaSuccess),
        ];

        if (!isUpdate) {
            if (steps.id === STEP_IDS.retrievePhone) {
                yieldArray.push(
                    call(gaEvent, {
                        category: GA_ESTIMA_FORM_CATEGORY,
                        action: `estima:${source}:${
                            STEPS[steps.number - 1].taggingId
                        }:submit_success`,
                        label: `from:${getSource()}`,
                    }),
                );
            }

            yieldArray.push(
                call(
                    gaEvent,
                    {
                        category: GA_ESTIMA_FORM_CATEGORY,
                        action: `estima:${source}:completed`,
                        label: `from:${getSource()}`,
                    },
                    gaCompletedTimeout,
                ),
            );
        } else {
            gaCompletedTimeout();
        }

        yield all(yieldArray);
    } else {
        gaCompletedTimeout();
    }

    if (!redirect) {
        yield put(actions.nextStep());
    }
}

/**
 * Receive form validation and enabled next step or not.
 * @yield {Action} Retrieving form from state
 * @yield {Action} Retrieving step from state
 * @yield {Action} Changing step validation
 */
export function* fetchCitiesSaga(action) {
    try {
        const response = yield call(GeoService.getCitiesFromZip, action.zip);
        const { places } = response.data.response;

        yield put(actions.getCitiesFromZipReceived(places));

        if (places.length === 1) {
            const place = places[0];

            yield all([
                put(change(ESTIMA_FORM, FORM_CITY_NAME, place.name_short)),
                put(actions.updateAddressMapCoordinates(place._geoloc, ZOOM_FOR_CITY)),
            ]);
        }
    } catch (err) {
        yield put(actions.getCitiesFromZipFailed(err));
    }
}

/**
 * Resolve city if from form, state and action
 * @param  {Object} form         Current form values
 * @param  {Object} addressState Current address state
 * @param  {Object} action       Action dispatched
 * @return {Integer}             City id
 */
function getCityId(form, addressState, action) {
    let cityId = null;

    if (form.values._city_id_from_url && addressState.placesOptions.length === 0) {
        cityId = Number(form.values._city_id_from_url);
    } else if (addressState.placesOptions.length > 0) {
        // Resolve user selected city to get city name of an arrmun or sucity
        const cityOption = addressState.placesOptions.find(
            (option) => option.label === action.cityName,
        );
        if (cityOption) {
            cityId = cityOption.id;
        }
    } else if (form.values._city_id) {
        cityId = form.values._city_id;
    }

    return cityId;
}

/**
 * Get city information from GeoService
 * @param {Object} form          Form state
 * @param {Object} addressState  Address state
 * @param {Object} action        Action dispatched
 */
export function* getCity(form, addressState, action) {
    try {
        const cityId = getCityId(form, addressState, action);
        if (!cityId) {
            return undefined;
        }

        const response = yield call(GeoService.getCityOrArmunOrSubcity, cityId);

        let selectedCity = response.data.response.place;
        if (selectedCity.city) {
            // Arrmun or subcity
            selectedCity = selectedCity.city;
        }

        selectedCity.coordinates = {
            lat: response.data.response.place.lat,
            lng: response.data.response.place.lng,
        };

        return selectedCity;
    } catch (err) {
        yield put(actions.getAddressesFailed(err));
        return undefined;
    }
}

/**
 * Get addresses from GeoService
 * @param {String} addressName   Address name
 * @param {String} cityName      City name
 * @param {Number} zip           Zipcode
 */
export function* getAddresses(addressName, cityName, zip) {
    try {
        const response = yield call(GeoService.getAddresses, addressName, cityName, zip);

        return response.data.response.places;
    } catch (err) {
        yield put(actions.getAddressesFailed(err));
        return undefined;
    }
}

/**
 * Receive form validation and enabled next step or not.
 * @yield {Action} Retrieving form from state
 * @yield {Action} Retrieving step from state
 * @yield {Action} Changing step validation
 */
export function* fetchAddressesSaga(action) {
    const addressState = yield select(addressSelector);
    const form = yield select(formSelector);

    const selectedCity = yield* getCity(form, addressState, action);
    if (!selectedCity) {
        return;
    }

    const addresses = yield* getAddresses(action.addressName, selectedCity.name_short, action.zip);

    try {
        const addressPlace = addresses ? addresses[0] : undefined;
        const addressWordDistance = addressPlace
            ? getEditDistance(
                  addressPlace.title.toLowerCase(),
                  `${action.addressName}, ${selectedCity.name_short}`.toLowerCase(),
              )
            : null;

        if (addressWordDistance !== null && addressWordDistance <= MAX_ADDRESS_WORD_DISTANCE) {
            // Perfect match
            yield all([
                put(actions.updateAddressMapCoordinates(addressPlace._geoloc)),
                put(actions.updateSuggestedAddresses([])),
                put(change(ESTIMA_FORM, 'coordinates', addressPlace._geoloc)),
                put(change(ESTIMA_FORM, '_suggested_address', null)),
            ]);
        } else if (addressWordDistance !== null) {
            // No Perfect match, user have to choice suggested address on next step
            yield all([
                put(actions.updateAddressMapCoordinates(selectedCity.coordinates)),
                put(actions.updateSelectedCoordinates(selectedCity.coordinates)),
                put(change(ESTIMA_FORM, 'coordinates', {})),
                put(change(ESTIMA_FORM, '_suggested_address', null)),
                put(
                    actions.updateSuggestedAddresses([
                        {
                            coordinates: addressPlace._geoloc,
                            addressName: addressPlace.name_short,
                            cityName: addressPlace.city_name,
                            zip: addressPlace?.zips?.length > 0 ? addressPlace.zips[0] : '',
                        },
                    ]),
                ),
            ]);
        } else if (addressWordDistance === null) {
            // Address not found, no suggestion, fallback to city coordinates
            yield all([
                put(change(ESTIMA_FORM, 'coordinates', selectedCity.coordinates)),
                put(change(ESTIMA_FORM, '_suggested_address', null)),
                put(actions.updateSuggestedAddresses([])),
            ]);
        }

        yield put(actions.getAddressesReceived(addresses));
        if (isValidAddress(action)) {
            yield put(actions.validStep(null, true));
        }
    } catch (err) {
        yield put(actions.getAddressesFailed(err));
    }
}

/**
 * Receive form validation and enabled next step or not.
 * @yield {Action} Retrieving step from state
 * @yield {Action} Changing step validation
 */
export function* formUnvalidateSaga(action) {
    const step = yield select(stepsSelector);

    if (action.payload === '' && step.id !== STEP_IDS.retrievePhone) {
        yield put(actions.validStep(null, false));
    }
}

/**
 * Update form value phoneStepReauired
 * @yield {Action} Retrieving form from state
 * @yield {Action} Retrieving step from state
 * @yield {Action} Change form value
 */
export function* checkPhoneStepRequiredSaga() {
    const form = yield select(formSelector);
    const auth = yield select(authSelector);

    yield put(
        change(ESTIMA_FORM, '_phoneStepRequired', hasPhoneRetrieval({ auth, values: form.values })),
    );
}

/**
 * Sagas of app.
 * @yield {Function} Sagas
 */
export default function* appSaga() {
    yield takeLatest(actions.STEPS_READY, gaFormEventSaga);
    yield takeLatest(actions.STEPS_READY, checkAuthSaga);
    yield takeLatest(actions.REQUESTED_GET_CITIES_FROM_ZIP, fetchCitiesSaga);
    yield takeLatest(actions.REQUESTED_GET_ADDRESSES, fetchAddressesSaga);
    yield takeLatest(actions.PREVIOUS_STEP, previousStepSaga);
    yield takeLatest(actions.NEXT_STEP, nextStepSaga);
    yield takeLatest(actions.CHANGE_STEP, gaEventStepSaga);
    yield takeLatest(actions.GA_STEP, gaStepSaga);
    yield takeLatest(actions.CHANGE_STEP, callAuthSaga);
    yield takeLatest(actions.CHANGE_STEP, callZoneMediaSaga);
    yield takeLatest(actions.REQUESTED_GET_HABITATIONS, getHabitationsSaga);

    yield takeLatest(actions.SAVE_ESTIMA_ID, submitSuccessSaga);
    yield takeLatest(
        [actions.RECEIVED_CHECK_ZONE_MEDIA, actions.RECEIVED_CHECK_AUTH],
        checkPhoneStepRequiredSaga,
    );

    /* Redux-form */
    yield takeLatest(
        [formActions.BLUR, formActions.CHANGE, actions.CHANGE_STEP, actions.RECEIVED_CHECK_AUTH],
        isLastStepSaga,
    );

    yield takeLatest(formActions.STOP_ASYNC_VALIDATION, formValidationSaga);
    yield takeLatest([formActions.CHANGE, formActions.BLUR], formUnvalidateSaga);
    yield takeLatest(formActions.SUBMIT, gaFormSubmitSaga);

    /* Barometre */
    yield takeLatest(actions.GO_TO_STEPS_FORM, goToStepsFormSaga);
    yield takeLatest(actions.REQUESTED_GET_ESTIMA, getEstimaSaga);
    yield takeLatest(actions.BAROMETRE_READY, gaBarometreEventSaga);
    yield takeLatest(actions.BAROMETRE_READY, checkAuthSaga);
    yield takeLatest(actions.BAROMETRE_READY, checkZoneMediaSaga);
    yield takeLatest(
        actions.GA_BAROMETRE_RETRIEVE_PHONE_STEP,
        gaBarometreRetrievePhoneStepEventSaga,
    );

    /* Submit sagas */
    yield takeLatest(actions.GA_SUBMIT, gaSubmitSaga);
}
